/**
 * UTM Text Custom Element
 * Handles client-side UTM matching and persistence across the user's session
 */
class UtmText extends HTMLElement {
    connectedCallback() {
        const utmParameter = this.dataset.utmParameter;
        const utmValue = this.dataset.utmValue;

        // Skip if no UTM data is provided
        if (!utmParameter || !utmValue) return;

        // Parse current URL parameters
        const currentUrlParams = this.getCurrentUrlParams();
        
        // Check if current URL matches this UTM parameter-value pair
        const matchesCurrentUrl = this.urlMatchesUtm(currentUrlParams, utmParameter, utmValue);
        
        // Check if this UTM was previously matched in this session
        const matchesStoredUtm = this.hasStoredUtmMatch(utmParameter, utmValue);

        // Show announcement if it matches current URL OR was previously matched
        if (matchesCurrentUrl || matchesStoredUtm) {
            this.removeAttribute('hidden');
            
            // If currently matching URL, store it for future page views
            if (matchesCurrentUrl) {
                this.storeUtmMatch(utmParameter, utmValue);
            }
            
            // Immediately remove all static announcements (no delay)
            const staticAnnouncements = document.querySelectorAll('announcement-bar static-text');
            staticAnnouncements.forEach(element => {
                element.remove();
            });
        } else {
            // Remove from DOM if it doesn't match (Swiper doesn't recognize hidden attribute)
            this.remove();
        }
        
        // Notify parent that evaluation is complete
        this.dispatchEvent(new CustomEvent('utm-evaluated', { bubbles: true }));
    }

    /**
     * Parse the current page URL and extract all query parameters
     * @returns {Object} Key-value pairs of URL parameters (lowercase, decoded values)
     */
    getCurrentUrlParams() {
        const params = {};
        const searchParams = new URLSearchParams(window.location.search);
        
        for (let [key, value] of searchParams) {
            // Convert to lowercase to match Liquid's downcase filter
            // URLSearchParams automatically decodes values, so we get the raw string
            params[key.toLowerCase()] = value.toLowerCase();
        }
        
        return params;
    }

    /**
     * Check if the current URL parameters match the specified UTM parameter-value pair
     * @param {Object} urlParams - Object containing decoded URL parameters
     * @param {string} parameter - The UTM parameter to check (e.g., 'utm_source')
     * @param {string} value - The expected raw value for the parameter (from Liquid, unencoded)
     * @returns {boolean} True if the URL contains the exact parameter=value pair
     */
    urlMatchesUtm(urlParams, parameter, value) {
        return urlParams[parameter] === value;
    }

    /**
     * Store a matched UTM parameter-value pair in sessionStorage
     * Uses an array to support multiple different values for the same parameter
     * @param {string} parameter - The UTM parameter name (e.g., 'utm_source')
     * @param {string} value - The UTM parameter value (e.g., 'lewis')
     */
    storeUtmMatch(parameter, value) {
        try {
            // Retrieve existing UTM matches from sessionStorage
            const storedUtms = sessionStorage.getItem('matched_utms');
            const utmMatches = storedUtms ? JSON.parse(storedUtms) : [];

            // Create a unique key for this parameter-value combination
            const matchKey = `${parameter}=${value}`;

            // Only store if not already present
            if (!utmMatches.includes(matchKey)) {
                utmMatches.push(matchKey);
                sessionStorage.setItem('matched_utms', JSON.stringify(utmMatches));
            }
        } catch (error) {
            console.error('Failed to store UTM match:', error);
        }
    }

    /**
     * Check if a UTM parameter-value pair was previously matched
     * @param {string} parameter - The UTM parameter name to check
     * @param {string} value - The UTM parameter value to check
     * @returns {boolean} True if this exact parameter-value pair was previously matched
     */
    hasStoredUtmMatch(parameter, value) {
        try {
            const storedUtms = sessionStorage.getItem('matched_utms');
            if (!storedUtms) return false;

            const utmMatches = JSON.parse(storedUtms);
            const matchKey = `${parameter}=${value}`;
            
            // Check if this exact parameter=value combination was stored
            return utmMatches.includes(matchKey);
        } catch (error) {
            console.error('Failed to check stored UTM match:', error);
            return false;
        }
    }
}

/**
 * Announcement Bar Custom Element
 * Handles the Swiper carousel initialization and announcement bar height calculations
 */
class AnnouncementBar extends HTMLElement {
    connectedCallback() {
        // Ensure Swiper container exists
        const swiperContainer = this.querySelector('.swiper');
        if (!swiperContainer) return;

        // Use a small delay to ensure UTM elements have been evaluated and removed if needed
        setTimeout(() => {
            this.initializeSwiper(swiperContainer);
        }, 50);
    }

    initializeSwiper(swiperContainer) {
        // Wait for Swiper to be ready
        whenReady('Swiper', () => {
            try {
                // Check how many slides are left in the DOM after UTM evaluation
                const slides = swiperContainer.querySelectorAll('.swiper-slide');
                const slideCount = slides.length;


                const swiper = new Swiper(swiperContainer, {
                    effect: 'fade',
                    loop: slideCount > 1, // Only loop if there are multiple slides
                    autoplay: slideCount > 1 ? {
                        delay: 3500,
                        disableOnInteraction: false,
                    } : false,
                    fadeEffect: {
                        crossFade: true,
                    },
                    pauseOnMouseEnter: false,
                    modules: [Swiper.EffectFade, Swiper.Autoplay],
                });

                // Avoid duplicate event bindings
                if (!this.hasInitialized) {
                    this.hasInitialized = true;
                    // console.log(this.querySelector('.flex-centered').clientHeight)
                    if(this.querySelector('.flex-centered') !== null){
                        document.documentElement.style.setProperty("--announcement-height", `${this.querySelector('.flex-centered').clientHeight}px`);
                    }
                }

                // Initial header height update
            } catch (error) {
                console.error('Failed to initialize Swiper:', error);
            }
        });
    }

    disconnectedCallback() {
        // Cleanup logic if needed
        this.hasInitialized = false; // Reset initialization flag
    }
}

// Define the custom elements if not already defined
if (!window.customElements.get('utm-text')) {
    window.customElements.define('utm-text', UtmText);
}

if (!window.customElements.get('announcement-bar')) {
    window.customElements.define('announcement-bar', AnnouncementBar);
}